import {
    OverlayTrigger,
    Table,
    Tooltip,
    Pagination,
    Modal,
    Button,
} from "react-bootstrap";
import { Check, X } from "react-feather";
import { formatNumberWithCommas } from "../../../../../utils/utility";
import { useState } from "react";

export default function List({
    writeOffs = [],
    currentPage = 1,
    perPage = 0,
    lastPage = 1, // total pages from API
    onPageChange,
    onApprove,
    onReject,
}) {
    const [showModal, setShowModal] = useState(false);
    const [selectedWriteOff, setSelectedWriteOff] = useState(null);
    const [actionType, setActionType] = useState(""); // "approve" or "reject"
    const handlePageChange = (page) => {
        if (page >= 1 && page <= lastPage) {
            onPageChange(page);
        }
    };

    const handleActionClick = (writeOff, type) => {
        setSelectedWriteOff(writeOff);
        setActionType(type);
        setShowModal(true);
    };

    const handleConfirmAction = () => {
        if (actionType === "approve" && onApprove) {
            onApprove(selectedWriteOff);
        } else if (actionType === "reject" && onReject) {
            onReject(selectedWriteOff);
        }
        setShowModal(false);
        setSelectedWriteOff(null);
        setActionType("");
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedWriteOff(null);
        setActionType("");
    };

    const renderPagination = () => {
        if (!Number.isFinite(lastPage) || lastPage <= 1) return null;

        const pageNumbers = Array.from({ length: lastPage }, (_, i) => i + 1);

        return (
            <Pagination className="justify-content-end mt-3 mb-0">
                <Pagination.Prev
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                />
                {pageNumbers.map((num) => (
                    <Pagination.Item
                        key={num}
                        active={num === currentPage}
                        onClick={() => handlePageChange(num)}
                    >
                        {num}
                    </Pagination.Item>
                ))}
                <Pagination.Next
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === lastPage}
                />
            </Pagination>
        );
    };

    // Sticky style for table headers
    const stickyHeaderStyle = {
        position: "sticky",
        top: "3rem", // adjust if top pagination height changes
        background: "#fff",
        zIndex: 2,
    };

    return (
        <div style={{ position: "relative" }}>
            {/* Top Pagination */}
            <div
                style={{
                    position: "sticky",
                    top: 0,
                    background: "#fff",
                    zIndex: 3,
                    padding: "0.5rem 0",
                }}
            >
                {renderPagination()}
            </div>

            {/* Table */}
            <Table striped bordered hover>
                <thead className="table-light">
                    <tr>
                        {[
                            "S.No",
                            "Actions",
                            "Type",
                            "Number",
                            "Original Amount",
                            "Remaining Amount",
                            "Created By",
                            "Created At",
                            "Approved By",
                            "Approved At",
                            "Status",
                        ].map((text, idx) => (
                            <th key={idx} style={stickyHeaderStyle}>
                                {text}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {writeOffs?.length === 0 ? (
                        <tr>
                            <td colSpan={15} className="text-center">
                                No data available
                            </td>
                        </tr>
                    ) : (
                        writeOffs?.map((data, index) => (
                            <tr key={data.id ?? index}>
                                <td>
                                    {(currentPage - 1) * perPage + index + 1}
                                </td>
                                <td>
                                    <div className="d-flex gap-2">
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip>Approve</Tooltip>}
                                        >
                                            <Button
                                                variant="success"
                                                size="sm"
                                                onClick={() =>
                                                    handleActionClick(
                                                        data,
                                                        "approve"
                                                    )
                                                }
                                                disabled={
                                                    data.status !== "PENDING"
                                                }
                                            >
                                                <Check size={16} />
                                            </Button>
                                        </OverlayTrigger>
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip>Reject</Tooltip>}
                                        >
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() =>
                                                    handleActionClick(
                                                        data,
                                                        "reject"
                                                    )
                                                }
                                                disabled={
                                                    data.status !== "PENDING"
                                                }
                                            >
                                                <X size={16} />
                                            </Button>
                                        </OverlayTrigger>
                                    </div>
                                </td>
                                <td>{data.type}</td>
                                <td>
                                    {data.type === "OVERPAID" ? (
                                        data.bank_transaction_id ? (
                                            <a
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href={`/tms/bank-transactions/payment/${data.bank_transaction_id}`}
                                            >
                                                {data.bank_transaction_number}
                                            </a>
                                        ) : (
                                            data.bank_transaction_number ?? "-"
                                        )
                                    ) : data.invoice_id ? (
                                        <a
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            href={`/tms/invoices/payments/${data.invoice_id}`}
                                        >
                                            {data.invoice_number}
                                        </a>
                                    ) : (
                                        data.invoice_number ?? "-"
                                    )}
                                </td>

                                <td>
                                    S${" "}
                                    {formatNumberWithCommas(
                                        data.original_amount ?? 0
                                    )}
                                </td>
                                <td>
                                    S${" "}
                                    {formatNumberWithCommas(
                                        data.remaining_amount ?? 0
                                    )}
                                </td>

                                <td>{data.requestor_name ?? "-"}</td>

                                <td>
                                    {data.created_at
                                        ? new Date(
                                              data.created_at
                                          ).toLocaleString()
                                        : "-"}
                                </td>

                                <td>{data.approver_name ?? "-"}</td>

                                <td>
                                    {data.approved_at
                                        ? new Date(
                                              data.approved_at
                                          ).toLocaleString()
                                        : "-"}
                                </td>

                                <td>{data.status}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>

            {/* Bottom Pagination */}
            <div
                style={{
                    position: "sticky",
                    bottom: 0,
                    background: "#fff",
                    zIndex: 3,
                    padding: "0.5rem 0",
                }}
            >
                {renderPagination()}
            </div>

            {/* Confirmation Modal */}
            {/* Confirmation Modal */}
            <Modal
                show={showModal}
                onHide={handleCloseModal}
                centered
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Action</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <p>
                        Are you sure you want to{" "}
                        <strong>
                            {actionType === "approve" ? "approve" : "reject"}
                        </strong>{" "}
                        this write-off request?
                    </p>

                    {selectedWriteOff && (
                        <div className="mt-3">
                            {/* INVOICE */}
                            {selectedWriteOff.type !== "OVERPAID" ? (
                                <>
                                    <div className="mb-2">
                                        <strong>Invoice:</strong>{" "}
                                        {selectedWriteOff.invoice_id ? (
                                            <a
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href={`/tms/invoices/payments/${selectedWriteOff.invoice_id}`}
                                            >
                                                {
                                                    selectedWriteOff.invoice_number
                                                }
                                            </a>
                                        ) : (
                                            selectedWriteOff.invoice_number ??
                                            "-"
                                        )}
                                    </div>

                                    <div className="mb-2">
                                        <strong>Amount:</strong> S${" "}
                                        {formatNumberWithCommas(
                                            selectedWriteOff.remaining_amount ??
                                                0
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* BANK TRANSACTION */}
                                    <div className="mb-2">
                                        <strong>Bank Transaction:</strong>{" "}
                                        {selectedWriteOff.bank_transaction_id ? (
                                            <a
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href={`/tms/bank-transactions/payment/${selectedWriteOff.bank_transaction_id}`}
                                            >
                                                {
                                                    selectedWriteOff.bank_transaction_number
                                                }
                                            </a>
                                        ) : (
                                            selectedWriteOff.bank_transaction_number ??
                                            "-"
                                        )}
                                    </div>

                                    <div className="mb-2">
                                        <strong>Amount:</strong> S${" "}
                                        {formatNumberWithCommas(
                                            selectedWriteOff.remaining_amount ??
                                                0
                                        )}
                                    </div>

                                    <div className="mb-2">
                                        <strong>Type:</strong> {" "}
                                        {
                                           selectedWriteOff.bank_details['type'] ?? '-'
                                        }
                                    </div>

                                    
                                    {/* BANK DETAILS */}
                                    {selectedWriteOff.bank_details &&  selectedWriteOff.bank_details['type'] === 'REFUND' && (
                                        <div className="mb-3">
                                            <strong>Bank Details:</strong>

                                            <div className="border rounded p-2 mt-2 bg-light">
                                                {Object.entries(
                                                    selectedWriteOff.bank_details
                                                ).map(([key, value]) => (
                                                    <div
                                                        key={key}
                                                        className="mb-1"
                                                    >
                                                        <strong>
                                                            {key
                                                                .replace(
                                                                    /_/g,
                                                                    " "
                                                                )
                                                                .replace(
                                                                    /\b\w/g,
                                                                    (c) =>
                                                                        c.toUpperCase()
                                                                )}
                                                            :
                                                        </strong>{" "}
                                                        {Array.isArray(value)
                                                            ? value.join(", ")
                                                            : String(
                                                                  value ?? "-"
                                                              )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* ATTACHMENTS */}
                                    {selectedWriteOff?.attachments
                                        ?.length > 0 && (
                                        <div className="mb-2">
                                            <strong>Supporting Document:</strong>

                                            <div className="d-flex flex-column gap-1 mt-2">
                                                {selectedWriteOff.attachments.map(
                                                    (attachment, index) => (
                                                        <a
                                                            key={index}
                                                            href={
                                                                attachment.full_path
                                                            }
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {attachment.file_name ??
                                                                `Attachment ${
                                                                    index + 1
                                                                }`}
                                                        </a>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Cancel
                    </Button>

                    <Button
                        variant={
                            actionType === "approve" ? "success" : "danger"
                        }
                        onClick={handleConfirmAction}
                    >
                        {actionType === "approve" ? "Approve" : "Reject"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
